export type OrderScriptLogAnalysisResult = {
  ordersDuration: number[];
  databaseSyncDuration: number[];
  orderCycleDurations: number[];
  syncCycleDurations: number[];
  orderCycleDuration: number;
  syncCycleDuration: number;
};

export const parseOrderScriptLogs = (
  logs: string,
): OrderScriptLogAnalysisResult => {
  const logEntries = logs.split("\n").filter((line) => line.trim() !== "");

  const ordersDuration: number[] = [];
  const databaseSyncDuration: number[] = [];

  const lastOrderCreatorTimes = new Map<string, number>();
  const lastSyncAccountTimes = new Map<string, number>();

  const orderCycleDurations: number[] = [];
  const syncCycleDurations: number[] = [];

  let currentOrderCycleStart: number | null = null;
  let currentOrderCycleEnd: number | null = null;
  let currentSyncCycleStart: number | null = null;
  let currentSyncCycleEnd: number | null = null;
  let lastEventType: "order" | "sync" | null = null;

  const parseTime = (line: string) => {
    const timeMatch = line.match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
    if (!timeMatch) return null;
    const [, hours, minutes, seconds, milliseconds] = timeMatch;
    return (
      parseInt(hours, 10) * 3600000 +
      parseInt(minutes, 10) * 60000 +
      parseInt(seconds, 10) * 1000 +
      parseInt(milliseconds, 10)
    );
  };

  const normalizeDelta = (previous: number, current: number) => {
    if (current >= previous) return current - previous;
    return current + 24 * 3600000 - previous; // handle midnight rollover
  };

  const finalizeOrderCycle = () => {
    if (currentOrderCycleStart !== null && currentOrderCycleEnd !== null) {
      orderCycleDurations.push(
        normalizeDelta(currentOrderCycleStart, currentOrderCycleEnd),
      );
    }
    currentOrderCycleStart = null;
    currentOrderCycleEnd = null;
  };

  const finalizeSyncCycle = () => {
    if (currentSyncCycleStart !== null && currentSyncCycleEnd !== null) {
      syncCycleDurations.push(
        normalizeDelta(currentSyncCycleStart, currentSyncCycleEnd),
      );
    }
    currentSyncCycleStart = null;
    currentSyncCycleEnd = null;
  };

  for (const line of logEntries) {
    if (!line.match(/^[iWE] /)) continue;

    const currentTime = parseTime(line);
    if (currentTime === null) continue;

    const accountMatch = line.match(/\[([^\]]+)\]/);
    const account = accountMatch ? accountMatch[1] : null;
    if (!account) continue;

    if (line.includes("===== Order Creator started for account")) {
      if (lastEventType === "sync") {
        finalizeSyncCycle();
      }
      lastEventType = "order";

      if (currentOrderCycleStart === null) {
        currentOrderCycleStart = currentTime;
      }
      currentOrderCycleEnd = currentTime;

      const lastTime = lastOrderCreatorTimes.get(account);
      if (lastTime !== undefined) {
        ordersDuration.push(normalizeDelta(lastTime, currentTime));
      }
      lastOrderCreatorTimes.set(account, currentTime);
    }

    if (line.includes("===== sync account started for account")) {
      if (lastEventType === "order") {
        finalizeOrderCycle();
      }
      lastEventType = "sync";

      if (currentSyncCycleStart === null) {
        currentSyncCycleStart = currentTime;
      }
      currentSyncCycleEnd = currentTime;

      const lastTime = lastSyncAccountTimes.get(account);
      if (lastTime !== undefined) {
        databaseSyncDuration.push(normalizeDelta(lastTime, currentTime));
      }
      lastSyncAccountTimes.set(account, currentTime);
    }
  }

  finalizeOrderCycle();
  finalizeSyncCycle();

  const calculateAverage = (durations: number[]) => {
    if (durations.length === 0) return 0;
    return durations.reduce((a, b) => a + b, 0) / durations.length;
  };

  return {
    ordersDuration,
    databaseSyncDuration,
    orderCycleDurations,
    syncCycleDurations,
    orderCycleDuration: calculateAverage(orderCycleDurations),
    syncCycleDuration: calculateAverage(syncCycleDurations),
  };
};
